import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import './ajuda.css';

/**
 * O "(?)" ao lado de um título, card ou campo: toque (ou passe o mouse) e aparece o que aquilo
 * é e de onde vem o número. Explica, não decide: nenhuma regra mora aqui.
 * O balão fica preso na tela (nunca sai pela borda do celular) e fecha com Esc ou toque fora.
 */
export function Ajuda({ children, rotulo }: { children: ReactNode; rotulo?: string }) {
  const id = useId();
  const botao = useRef<HTMLButtonElement>(null);
  const balao = useRef<HTMLSpanElement>(null);
  const [aberta, setAberta] = useState(false);
  const [fixa, setFixa] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const fechar = useCallback(() => {
    setAberta(false);
    setFixa(false);
  }, []);

  useLayoutEffect(() => {
    if (!aberta || !botao.current || !balao.current) return;
    const b = botao.current.getBoundingClientRect();
    const w = balao.current.offsetWidth;
    const h = balao.current.offsetHeight;
    const margem = 12;
    const left = Math.min(
      Math.max(margem, b.left + b.width / 2 - w / 2),
      window.innerWidth - w - margem,
    );
    // Abaixo do (?); sem espaço embaixo, acima.
    const top = b.bottom + 8 + h > window.innerHeight - margem ? b.top - 8 - h : b.bottom + 8;
    setPos({ top: Math.max(margem, top), left });
  }, [aberta]);

  useEffect(() => {
    if (!aberta) return;
    const fora = (e: Event) => {
      const alvo = e.target as Node;
      if (!botao.current?.contains(alvo) && !balao.current?.contains(alvo)) fechar();
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        fechar();
        botao.current?.focus();
      }
    };
    const rolou = () => fechar();
    document.addEventListener('pointerdown', fora);
    document.addEventListener('keydown', tecla);
    window.addEventListener('scroll', rolou, true);
    window.addEventListener('resize', rolou);
    return () => {
      document.removeEventListener('pointerdown', fora);
      document.removeEventListener('keydown', tecla);
      window.removeEventListener('scroll', rolou, true);
      window.removeEventListener('resize', rolou);
    };
  }, [aberta, fechar]);

  return (
    <span className="aj">
      <button
        ref={botao}
        type="button"
        className={`aj-botao${aberta ? ' aj-aberta' : ''}`}
        aria-label={rotulo ? `O que é ${rotulo}?` : 'O que é isto?'}
        aria-expanded={aberta}
        aria-describedby={aberta ? id : undefined}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (fixa) fechar();
          else {
            setFixa(true);
            setAberta(true);
          }
        }}
        onMouseEnter={() => setAberta(true)}
        onMouseLeave={() => {
          if (!fixa) setAberta(false);
        }}
      >
        ?
      </button>
      {aberta && (
        <span
          ref={balao}
          id={id}
          role="tooltip"
          className="aj-balao"
          style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
        >
          {children}
        </span>
      )}
    </span>
  );
}
